import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Typography, Card } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { ChevronLeft as BackIcon } from '@material-ui/icons';
import { grey } from '@material-ui/core/colors';
import moment from 'moment';
import { sortBy } from 'lodash';
import { apiGet } from './api';
import { useAccessTokens } from './Authenticate';
import NavLink from './components/NavLink';

const useStyles = makeStyles(() => ({
    card: {
        paddingBottom: '1px',
    },
    topCard: {
        padding: '10px 50px',
        backgroundColor: grey[100],
    },
    cardLabel: {
        fontWeight: '500',
        color: '#333333',
    },
    title: {
        paddingBottom: '16px',
    },
    returnButton: {
        display: 'inline-flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    loader: {
        marginTop: 75,
    },
    disassociated: {
        fontWeight: '500',
        color: '#b00020',
    },
}));

// The licensing service limits how many ids can be sent in one "inq" filter,
// so the related licenses are requested in chunks (same as ManageDependents).
const CHUNK_SIZE = 200;

function buildFilter(filterConfig, getValue) {
    if (filterConfig?.include) {
        return (item) => filterConfig.include.includes(getValue(item));
    }

    if (filterConfig?.exclude) {
        return (item) => !filterConfig.exclude.includes(getValue(item));
    }

    return () => true;
}

function formatAddress(address) {
    if (!address?.line1) {
        return '';
    }

    return `${address.line1}, ${address.city}, ${address.state} ${address.zipcode}`;
}

function formatDate(date) {
    return date ? moment(date).format('MM/DD/YYYY') : '--';
}

// A prerequisite is disassociated once its disassociation date (t_license_ref.date_of_expiration,
// returned as dateOfExpiration) is today or earlier. A future date means it is still associated.
function isDisassociated(prerequisite) {
    return !!prerequisite.dateOfExpiration && moment(prerequisite.dateOfExpiration).isSameOrBefore(moment(), 'day');
}

export default function ViewPrerequisites({ location, match }) {
    const classes = useStyles();
    const { getAccessToken, authorizationScopes } = useAccessTokens();

    // Optional site configuration, e.g.
    // "viewPrerequisites": { "title": "Parent Licenses", "relationshipTypeFilter": { "exclude": ["1"] },
    //                        "statusFilter": { "include": ["Active"] }, "hideDisassociated": true }
    const prerequisiteConfig = useSelector(({ siteInfo }) => siteInfo?.viewPrerequisites);

    const title = prerequisiteConfig?.title ?? 'Prerequisites';
    const titleLowerCase = title.toLowerCase();

    // The license is passed in router state from the action menu. If the page is
    // refreshed or opened directly, that state is gone, so load it by the URL id.
    const [license, setLicense] = useState(location.state?.license);
    // undefined = still loading.
    const [prerequisites, setPrerequisites] = useState();
    const [loadingFailed, setLoadingFailed] = useState(false);

    const licenseId = license?.id ?? match.params.id;

    useEffect(() => {
        if (license) {
            return;
        }

        (async function () {
            try {
                const token = await getAccessToken(authorizationScopes.licenses);
                setLicense((await apiGet(`licenses/${licenseId}`, token)).data);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error(err);
                setLoadingFailed(true);
            }
        })();
    }, [license, licenseId]);

    useEffect(() => {
        (async function () {
            try {
                const token = await getAccessToken(authorizationScopes.licenses);

                // 1. The relations: which licenses are prerequisites of this one.
                const relations = (await apiGet(`licenses/${licenseId}/prerequisites`, token)).data
                    .filter(
                        buildFilter(
                            prerequisiteConfig?.relationshipTypeFilter,
                            (relation) => relation.relationshipType,
                        ),
                    )
                    // Sites can choose to hide prerequisites that are no longer associated.
                    .filter((relation) => !prerequisiteConfig?.hideDisassociated || !isDisassociated(relation));

                // 2. The details of those licenses (number, type, status, holder).
                const relatedLicenses = await loadLicenses(
                    relations.map(({ licenseId }) => licenseId),
                    token,
                );

                // 3. Merge the relation with its license details.
                let prerequisitesWithDetails = relations.map((relation) => {
                    const prerequisiteLicense = relatedLicenses.find(({ id }) => id === relation.licenseId);

                    return {
                        ...relation,
                        licenseNumber: prerequisiteLicense?.licenseNumber,
                        licenseType: prerequisiteLicense?.type?.name,
                        licenseStatus: prerequisiteLicense?.status,
                        expirationDate: prerequisiteLicense?.expirationDate,
                        personName: prerequisiteLicense?.person?.name,
                    };
                });

                prerequisitesWithDetails = prerequisitesWithDetails.filter(
                    buildFilter(prerequisiteConfig?.statusFilter, (prerequisite) => prerequisite.licenseStatus),
                );

                setPrerequisites(sortBy(prerequisitesWithDetails, ['personName', 'licenseNumber']));
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error(err);
                setLoadingFailed(true);
                setPrerequisites([]);
            }
        })();
    }, [licenseId, prerequisiteConfig]);

    const loadLicenses = async (ids, token) => {
        let licenses = [];

        for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
            const chunk = ids.slice(i, i + CHUNK_SIZE);

            const response = await apiGet('licenses', token, null, {
                params: {
                    filter: {
                        where: { id: { inq: chunk } },
                        fields: {
                            id: true,
                            licenseNumber: true,
                            type: true,
                            status: true,
                            expirationDate: true,
                            person: true,
                        },
                    },
                },
            });

            licenses = licenses.concat(response.data);
        }

        return licenses;
    };

    return (
        <Grid item container spacing={4} xs={12} direction="row">
            <Grid item>
                <NavLink to="/" variant="subtitle1">
                    <span className={classes.returnButton}>
                        <BackIcon />
                        Dashboard
                    </span>
                </NavLink>
            </Grid>
            <Grid item container xs={12}>
                <Grid item xs={12} className={classes.title}>
                    <Typography variant="h4">{title}</Typography>
                </Grid>
                {license && (
                    <Grid container item xs={12} direction="column">
                        <Typography variant="subtitle1">{license.person?.name}</Typography>
                        <Typography variant="subtitle1">{formatAddress(license.address)}</Typography>
                        <Typography variant="subtitle1">
                            {license.type?.name ? `${license.type.name} - ` : ''}License number: {license.licenseNumber}
                        </Typography>
                    </Grid>
                )}
            </Grid>
            {!prerequisites ? (
                <Grid container xs={12} item justifyContent="center">
                    <CircularProgress className={classes.loader} />
                </Grid>
            ) : loadingFailed ? (
                <Grid container xs={12} item justifyContent="center">
                    <Typography variant="h6" color="error">{`We were unable to load the ${titleLowerCase}`}</Typography>
                </Grid>
            ) : !prerequisites.length ? (
                <Grid container xs={12} item justifyContent="center">
                    <Typography variant="h5">{`No ${titleLowerCase}`}</Typography>
                </Grid>
            ) : (
                <Grid container xs={12} item direction="row">
                    {prerequisites.map((prerequisite) => (
                        <PrerequisiteCard key={prerequisite.licenseId} prerequisite={prerequisite} />
                    ))}
                </Grid>
            )}
        </Grid>
    );
}

const PrerequisiteCard = React.memo(({ prerequisite }) => {
    const classes = useStyles();

    return (
        <Grid item xs={12} className={classes.card}>
            <Card>
                <Grid container item className={classes.topCard} xs={12}>
                    <Grid item container justifyContent="space-between">
                        <Grid item>
                            <Typography variant="h6" className={classes.cardLabel}>
                                {prerequisite.personName ?? '--'}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Typography variant="h6" className={classes.cardLabel}>
                                {prerequisite.relationshipTypeLabel ?? ''}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid item container justifyContent="space-between">
                        <Grid item>
                            <Typography variant="subtitle1">
                                {prerequisite.licenseNumber ?? '--'}
                                {', '}
                                {prerequisite.licenseType ?? '--'}
                                {', '}
                                {prerequisite.licenseStatus ?? '--'}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Typography variant="subtitle1">
                                Associated On {formatDate(prerequisite.dateOfAssociation)}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid item container justifyContent="space-between">
                        <Grid item>
                            <Typography variant="subtitle1">
                                License Expires {formatDate(prerequisite.expirationDate)}
                            </Typography>
                        </Grid>
                        {/* Only disassociated prerequisites show the disassociation date. */}
                        {isDisassociated(prerequisite) && (
                            <Grid item>
                                <Typography variant="subtitle1" className={classes.disassociated}>
                                    Disassociated On {formatDate(prerequisite.dateOfExpiration)}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Card>
        </Grid>
    );
});

// eslint complains if there's no display name.
PrerequisiteCard.displayName = 'PrerequisiteCard';
